import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';

import { Plus, Search, Loader2, Filter, Download, Sheet } from 'lucide-react';
import { toCSV, downloadCSV, openInGoogleSheets } from '../../../utils/csvExport';
import { getAllProducts, saveProduct } from '../../../services/firestore/formularyService';
import type { Product, ProductCategory, ProductStatus } from '../../../types/formulary';
import type { ClinicalTier } from '../../../types/index';

const TIER_COLORS: Record<number, string> = {
  1: 'text-optimal bg-green-50 border-green-200',
  2: 'text-gold bg-yellow-50 border-yellow-200',
  3: 'text-accent-light bg-red-50 border-red-200',
  4: 'text-purple-600 bg-purple-50 border-purple-200',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'text-optimal',
  draft: 'text-text-muted',
  discontinued: 'text-critical',
};

export function ProductList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState<ClinicalTier | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<ProductCategory | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<ProductStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    try {
      setLoading(true);
      const data = await getAllProducts();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterTier !== 'all' && p.tier !== filterTier) return false;
      if (filterCategory !== 'all' && p.category !== filterCategory) return false;
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      return true;
    });
  }, [products, search, filterTier, filterCategory, filterStatus]);

  async function toggleStatus(product: Product) {
    const newStatus: ProductStatus = product.status === 'active' ? 'draft' : 'active';
    const updated = { ...product, status: newStatus };
    try {
      await saveProduct(updated);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
    } catch (err) {
      alert('Failed to update status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-gold" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
        <p className="font-medium">Error loading products</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-display text-gold">Products</h2>
          <p className="text-base text-text-muted">{products.length} total, {filtered.length} shown</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const csv = toCSV(filtered.map((p) => ({
                '#': p.catalogNumber,
                Name: p.name,
                Category: p.category,
                Subcategory: p.subcategory,
                Tier: p.tier,
                Status: p.status,
                Gender: p.gender,
                Route: p.route,
                Dosage: p.dosage,
                Timing: p.timing,
                'Target Biomarkers': p.targetBiomarkers,
                Contraindications: p.contraindications,
                'Drug Interactions': p.drugInteractions,
                'Evidence Level': p.evidenceLevel,
                'Requires Rx': p.requiresRx,
                'Monthly Price': p.pricing?.monthlyPrice || '',
                'Clinical Notes': p.clinicalNotes,
              })), [
                { key: '#', label: '#' }, { key: 'Name', label: 'Name' },
                { key: 'Category', label: 'Category' }, { key: 'Subcategory', label: 'Subcategory' },
                { key: 'Tier', label: 'Tier' }, { key: 'Status', label: 'Status' },
                { key: 'Gender', label: 'Gender' }, { key: 'Route', label: 'Route' },
                { key: 'Dosage', label: 'Dosage' }, { key: 'Timing', label: 'Timing' },
                { key: 'Target Biomarkers', label: 'Target Biomarkers' },
                { key: 'Contraindications', label: 'Contraindications' },
                { key: 'Drug Interactions', label: 'Drug Interactions' },
                { key: 'Evidence Level', label: 'Evidence Level' },
                { key: 'Requires Rx', label: 'Requires Rx' },
                { key: 'Monthly Price', label: 'Monthly Price' },
                { key: 'Clinical Notes', label: 'Clinical Notes' },
              ]);
              downloadCSV(csv, 'hemologix-products.csv');
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors"
            title="Download CSV"
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={() => {
              const csv = toCSV(filtered.map((p) => ({
                '#': p.catalogNumber, Name: p.name, Category: p.category,
                Tier: p.tier, Status: p.status, Dosage: p.dosage,
                'Target Biomarkers': p.targetBiomarkers,
                Contraindications: p.contraindications,
                'Drug Interactions': p.drugInteractions,
                'Monthly Price': p.pricing?.monthlyPrice || '',
              })), [
                { key: '#', label: '#' }, { key: 'Name', label: 'Name' },
                { key: 'Category', label: 'Category' }, { key: 'Tier', label: 'Tier' },
                { key: 'Status', label: 'Status' }, { key: 'Dosage', label: 'Dosage' },
                { key: 'Target Biomarkers', label: 'Target Biomarkers' },
                { key: 'Contraindications', label: 'Contraindications' },
                { key: 'Drug Interactions', label: 'Drug Interactions' },
                { key: 'Monthly Price', label: 'Monthly Price' },
              ]);
              openInGoogleSheets(csv, 'Hemologix Products');
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors"
            title="Open in Google Sheets"
          >
            <Sheet size={16} className="text-optimal" />
            Sheets
          </button>
          <Link
            to="/admin/products/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors"
          >
            <Plus size={16} />
            Add Product
          </Link>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold/50"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
              showFilters ? 'border-gold/50 text-gold' : 'border-bg-tertiary text-text-secondary hover:text-text-primary'
            }`}
          >
            <Filter size={16} />
            Filters
          </button>
        </div>

        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-bg-tertiary"
          >
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value === 'all' ? 'all' : (Number(e.target.value) as ClinicalTier))}
              className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-sm text-text-primary focus:outline-none focus:border-gold/50"
            >
              <option value="all">All Tiers</option>
              <option value="1">Tier 1</option>
              <option value="2">Tier 2</option>
              <option value="3">Tier 3</option>
              <option value="4">Tier 4</option>
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as ProductCategory | 'all')}
              className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-sm text-text-primary focus:outline-none focus:border-gold/50"
            >
              <option value="all">All Categories</option>
              <option value="supplement">Supplement</option>
              <option value="rx">Rx</option>
              <option value="longevity">Longevity</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ProductStatus | 'all')}
              className="px-3 py-2 bg-bg-elevated border border-bg-tertiary rounded-lg text-sm text-text-primary focus:outline-none focus:border-gold/50"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </motion.div>
        )}
      </div>

      {/* Table */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl">
          <table className="w-full text-base">
            <thead className="sticky top-0 z-10" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.25)' }}>
              <tr className="border-b-2 border-gold/30 text-text-muted text-left">
                <th className="px-4 py-3 font-medium bg-bg-card">#</th>
                <th className="px-4 py-3 font-medium bg-bg-card">Name</th>
                <th className="px-4 py-3 font-medium bg-bg-card hidden md:table-cell">Category</th>
                <th className="px-4 py-3 font-medium bg-bg-card">Tier</th>
                <th className="px-4 py-3 font-medium bg-bg-card hidden sm:table-cell">Status</th>
                <th className="px-4 py-3 font-medium bg-bg-card hidden lg:table-cell">Gender</th>
                <th className="px-4 py-3 font-medium bg-bg-card hidden lg:table-cell">Evidence</th>
                <th className="px-4 py-3 font-medium bg-bg-card hidden xl:table-cell">Route</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product, i) => (
                <motion.tr
                  key={product.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.01 }}
                  onClick={() => navigate(`/admin/products/${product.id}`)}
                  className="border-b border-bg-tertiary/50 hover:bg-bg-elevated cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-text-muted">{product.catalogNumber}</td>
                  <td className="px-4 py-3 font-medium text-text-primary">{product.name}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="capitalize text-text-secondary">{product.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${TIER_COLORS[product.tier] || ''}`}>
                      T{product.tier}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStatus(product);
                      }}
                      className={`text-xs font-medium capitalize ${STATUS_COLORS[product.status] || ''} hover:underline`}
                    >
                      {product.status}
                    </button>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="capitalize text-text-secondary">{product.gender}</span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="capitalize text-text-secondary">{product.evidenceLevel}</span>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell">
                    <span className="capitalize text-text-secondary">{product.route}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-text-muted">
            <p>No products match your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
